import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useGlobalPlayer, type PlayerState } from '../hooks/useGlobalPlayer';
import {
  AuthUpdateEvent,
  BackendClient,
  VersionUpdateEvent,
} from '../util/backendClient';
import { CollectionSortBy } from '../util/collectionSort';
import { portalVersion } from '../version';

// Define the shape of the app set (matches AuthenticatedUserResponse user.appSets items)
export interface AppSet {
  productionApp: {
    id: number;
    displayName: string;
  };
  developmentApp: {
    id: number;
    displayName: string;
  };
  /** Feature flags enabled for this app instance set (e.g. CatalogEnabled) */
  featureFlags: string[];
}

// Define the shape of the user object
export interface StudioUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  appSets: AppSet[];
}

// Define the context shape
export interface StudioContextType {
  /** Populated after login */
  currentUser: StudioUser | null;

  /** Login the user and populate their data (instance sets, etc.) */
  login: (
    email: string,
    password: string,
    debugSettings?: {
      sessionDurationSec?: number;
      refreshDurationSec?: number;
    },
  ) => Promise<void>;

  /** Authenticate using a token and user data */
  authenticateWithToken: (token: string, user: StudioUser) => Promise<void>;

  /** Null the current user and session token and return to the login screen */
  logout: () => void;

  /** A list of the user's app sets */
  appSets: AppSet[] | null;

  /** Set the app sets */
  setAppSets: (appSets: AppSet[] | null) => void;

  /** The currently selected app set (defaults to the first app set) */
  selectedAppSet: AppSet | null;

  /** Set the selected app set */
  setSelectedAppSet: (appSet: AppSet | null) => void;

  /** Collection sort preference */
  collectionSort: CollectionSortBy;

  /** Set the collection sort preference */
  setCollectionSort: (sort: CollectionSortBy) => void;

  /** Global audio volume (0-100), persisted in localStorage */
  volume: number;

  /** Set global audio volume (0-100), persisted in localStorage */
  setVolume: (volume: number) => void;

  /** Global player state and controls */
  playerState: PlayerState;

  /** Class for interacting with the backend API */
  backendClient: BackendClient;

  /** Versions and environment info */
  portalVersion: string;
  servicesApiVersion: string | null;
  servicesEnvironment: 'local' | 'stage' | 'prod' | 'unknown';
}

export interface AuthenticatedStudioContextType extends StudioContextType {
  currentUser: StudioUser;
  appSets: AppSet[];
  selectedAppSet: AppSet;
}

const StudioContext = createContext<StudioContextType | null>(null);

/**
 * Use this hook when you want to access the context even if the user is not authenticated.
 * Technically "unauthenticated" is a misnomer as the user may HAPPEN to be authenticated, but this
 * hook does not require it. All context fields may be null.
 *
 * This is useful for login screens, password reset, etc.
 */
export const useStudioUnauthenticatedContext = () => {
  const context = useContext(StudioContext);

  if (!context) {
    throw new Error(
      'useStudioContext must be used within a StudioContextProvider',
    );
  }

  return context;
};

/**
 * Use this hook when you require the user to be authenticated.
 * Requires user and session token to be loaded.
 */
export const useStudioAuthenticatedContext = () => {
  const context = useStudioUnauthenticatedContext();

  if (!context.currentUser) {
    throw new Error(
      'useStudioAuthenticatedContext: User is not authenticated.',
    );
  }
  return context;
};

/**
 * Use this hook when you require the user to be authenticated and all context data to be loaded.
 * Requires user, session token, app sets, and selected app set.
 */
export const useStudioFullContext = () => {
  const context = useStudioUnauthenticatedContext();

  if (!context.currentUser || !context.appSets || !context.selectedAppSet) {
    throw new Error(
      'useStudioFullContext: User is not authenticated or context is not fully loaded.',
    );
  }
  return context;
};

interface StudioContextProviderProps {
  children: ReactNode;
}

// Helper functions for localStorage
const STORAGE_KEYS = {
  SESSION_TOKEN: 'studio_session_token',
  CURRENT_USER: 'studio_current_user',
  APP_SETS: 'studio_app_sets',
  SELECTED_APP_SET: 'studio_selected_app_set',
  COLLECTION_SORT: 'studio_collection_sort',
  VOLUME: 'studio_volume',
};

const getStoredSessionToken = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);
  } catch {
    return null;
  }
};

const getStoredCurrentUser = (): StudioUser | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

function normalizeAppSet(appSet: Partial<AppSet> | null): AppSet | null {
  if (!appSet?.productionApp || !appSet?.developmentApp) return null;
  return {
    ...appSet,
    productionApp: appSet.productionApp!,
    developmentApp: appSet.developmentApp!,
    featureFlags: Array.isArray(appSet.featureFlags) ? appSet.featureFlags : [],
  } as AppSet;
}

const getStoredAppSets = (): AppSet[] | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.APP_SETS);
    const parsed = stored ? JSON.parse(stored) : null;
    if (!Array.isArray(parsed)) return null;
    return parsed
      .map((item) => normalizeAppSet(item))
      .filter(Boolean) as AppSet[];
  } catch {
    return null;
  }
};

const getStoredSelectedAppSet = (): AppSet | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SELECTED_APP_SET);
    return normalizeAppSet(stored ? JSON.parse(stored) : null);
  } catch {
    return null;
  }
};

const getStoredCollectionSort = (): CollectionSortBy => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.COLLECTION_SORT);
    return stored === CollectionSortBy.NAME
      ? CollectionSortBy.NAME
      : CollectionSortBy.CREATE_DATE;
  } catch {
    return CollectionSortBy.CREATE_DATE;
  }
};

const getStoredVolume = (): number => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.VOLUME);
    const parsed = saved != null ? Number(saved) : NaN;
    if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 100) {
      return Math.round(parsed);
    }
  } catch {
    // ignore
  }
  return 100;
};

export const StudioContextProvider = (props: StudioContextProviderProps) => {
  // Initialize state from localStorage
  const [currentUser, setCurrentUser] = useState<StudioUser | null>(
    getStoredCurrentUser,
  );
  const [appSets, setAppSets] = useState<AppSet[] | null>(getStoredAppSets);
  const [selectedAppSet, setSelectedAppSet] = useState<AppSet | null>(
    getStoredSelectedAppSet,
  );
  const [sessionToken, setSessionToken] = useState<string | null>(
    getStoredSessionToken,
  );
  const [collectionSort, setCollectionSort] = useState<CollectionSortBy>(
    getStoredCollectionSort,
  );
  const [volume, setVolumeState] = useState<number>(getStoredVolume);
  const [servicesApiVersion, setServicesApiVersion] = useState<string | null>(
    null,
  );
  const [servicesEnvironment, setServicesEnvironment] = useState<
    'local' | 'stage' | 'prod' | 'unknown'
  >('unknown');

  // Use the global player hook
  const playerState = useGlobalPlayer({
    volume,
    setVolume: (newVolume: number) => {
      // Validate and clamp
      if (
        typeof newVolume !== 'number' ||
        !Number.isFinite(newVolume) ||
        Number.isNaN(newVolume)
      ) {
        return;
      }
      const clamped = Math.min(100, Math.max(0, Math.round(newVolume)));
      setVolumeState(clamped);
      try {
        localStorage.setItem(STORAGE_KEYS.VOLUME, String(clamped));
      } catch {
        // ignore storage failures
      }
    },
  });

  const backendClientRef = useRef<BackendClient | undefined>(undefined);

  if (!backendClientRef.current) {
    backendClientRef.current = new BackendClient(
      process.env.REACT_APP__SERVICES_BASE_URL || '',
      sessionToken,
    );
  }

  const backendClient = backendClientRef.current;

  // Initialize state from localStorage
  useEffect(() => {
    if (sessionToken) {
      backendClient.setAuthToken(sessionToken);
    }
  }, [sessionToken, backendClient]);

  const setSessionTokenWithStorage = (token: string | null) => {
    setSessionToken(token);

    // Persist to localStorage
    if (token) {
      localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, token);
    } else {
      localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
    }
  };

  const setCurrentUserWithStorage = (user: StudioUser | null) => {
    setCurrentUser(user);

    // Persist to localStorage
    if (user) {
      localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
    } else {
      localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
    }
  };

  const setAppSetsWithStorage = (sets: AppSet[] | null) => {
    setAppSets(sets);

    // Persist to localStorage
    if (sets) {
      localStorage.setItem(STORAGE_KEYS.APP_SETS, JSON.stringify(sets));
    } else {
      localStorage.removeItem(STORAGE_KEYS.APP_SETS);
    }
  };

  const setSelectedAppSetWithStorage = (appSet: AppSet | null) => {
    setSelectedAppSet(appSet);

    // Persist to localStorage
    if (appSet) {
      localStorage.setItem(
        STORAGE_KEYS.SELECTED_APP_SET,
        JSON.stringify(appSet),
      );
    } else {
      localStorage.removeItem(STORAGE_KEYS.SELECTED_APP_SET);
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to revoke refresh token
      if (sessionToken) {
        await backendClient.logout();
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Clear all auth data regardless of logout success
      setCurrentUserWithStorage(null);
      setAppSetsWithStorage(null);
      setSelectedAppSetWithStorage(null);
      backendClient.setAuthToken(null);
    }
  };

  /** Invoked when the backend client receives an auth update event. */
  const handleAuthUpdate = useCallback(
    async (event: AuthUpdateEvent) => {
      const response = event.detail;
      if (!response) {
        logout();
        return;
      }

      // Update the session token
      setSessionTokenWithStorage(response.token);

      // Update user data
      setCurrentUserWithStorage({
        id: response.user.id,
        email: response.user.email,
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        appSets: response.user.appSets,
      });

      // Update app sets
      setAppSetsWithStorage(response.user.appSets);

      // Update selected app set if needed
      if (!selectedAppSet && response.user.appSets.length > 0) {
        setSelectedAppSetWithStorage(response.user.appSets[0]);
      }
    },
    [
      selectedAppSet,
      setSessionTokenWithStorage,
      setCurrentUserWithStorage,
      setAppSetsWithStorage,
      setSelectedAppSetWithStorage,
      logout,
    ],
  );

  // Set up callbacks for auth updates
  useEffect(() => {
    backendClient.events.addAuthListener(handleAuthUpdate);
    return () => backendClient.events.removeAuthListener(handleAuthUpdate);
  }, [backendClient, handleAuthUpdate]);

  /** Invoked when the backend client receives a version update event. */
  const handleBackendVersionUpdate = useCallback(
    async (event: VersionUpdateEvent) => {
      const { servicesApiVersion, environment } = event.detail;
      setServicesApiVersion(servicesApiVersion);
      setServicesEnvironment(environment);
    },
    [setServicesApiVersion, setServicesEnvironment],
  );

  // Set up callbacks for version updates
  useEffect(() => {
    backendClient.events.addVersionListener(handleBackendVersionUpdate);
    return () =>
      backendClient.events.removeVersionListener(handleBackendVersionUpdate);
  }, [backendClient, handleBackendVersionUpdate]);

  const setCollectionSortWithStorage = (sort: CollectionSortBy) => {
    setCollectionSort(sort);
    localStorage.setItem(STORAGE_KEYS.COLLECTION_SORT, sort);
  };

  const authenticateWithToken = async (token: string, user: StudioUser) => {
    try {
      // Set the token for API authentication
      setSessionTokenWithStorage(token);
      setCurrentUserWithStorage(user);

      // Set app sets from user data
      setAppSetsWithStorage(user.appSets);

      // Set default selected app set to the first one
      const defaultAppSet = user.appSets.length > 0 ? user.appSets[0] : null;
      setSelectedAppSetWithStorage(defaultAppSet);
    } catch (error) {
      // If token validation fails, clear the token
      setSessionTokenWithStorage(null);
      setCurrentUserWithStorage(null);
      setAppSetsWithStorage(null);
      setSelectedAppSetWithStorage(null);
      throw error;
    }
  };

  const login = async (
    email: string,
    password: string,
    debugSettings?: {
      sessionDurationSec?: number;
      refreshDurationSec?: number;
    },
  ) => {
    try {
      // Call the actual login endpoint
      const loginResponse = await backendClient.login(
        email,
        password,
        debugSettings,
      );

      // Set the JWT token for future API calls
      setSessionTokenWithStorage(loginResponse.token);

      // Set the current user from the response
      setCurrentUserWithStorage({
        id: loginResponse.user.id,
        email: loginResponse.user.email,
        firstName: loginResponse.user.firstName,
        lastName: loginResponse.user.lastName,
        appSets: loginResponse.user.appSets,
      });

      // Set app sets from the response
      setAppSetsWithStorage(loginResponse.user.appSets);

      // Set default selected app set to the first one
      const defaultAppSet =
        loginResponse.user.appSets.length > 0
          ? loginResponse.user.appSets[0]
          : null;
      setSelectedAppSetWithStorage(defaultAppSet);
    } catch (error) {
      // Clear any existing auth data on login failure
      setSessionTokenWithStorage(null);
      setCurrentUserWithStorage(null);
      setAppSetsWithStorage(null);
      setSelectedAppSetWithStorage(null);
      throw error;
    }
  };

  return (
    <StudioContext.Provider
      value={{
        currentUser,
        login,
        authenticateWithToken,
        logout,
        appSets,
        setAppSets: setAppSetsWithStorage,
        selectedAppSet,
        setSelectedAppSet: setSelectedAppSetWithStorage,
        collectionSort,
        setCollectionSort: setCollectionSortWithStorage,
        volume,
        setVolume: (v: number) => {
          // Keep context methods consistent even if playerState also exposes setVolume
          if (typeof v !== 'number' || !Number.isFinite(v) || Number.isNaN(v)) {
            return;
          }
          const clamped = Math.min(100, Math.max(0, Math.round(v)));
          setVolumeState(clamped);
          try {
            localStorage.setItem(STORAGE_KEYS.VOLUME, String(clamped));
          } catch {
            // ignore storage failures
          }
        },
        playerState,
        backendClient,
        portalVersion,
        servicesApiVersion,
        servicesEnvironment,
      }}
    >
      {props.children}
    </StudioContext.Provider>
  );
};
